<template>
  <div class="maintenance" :class="{ 'is-vehicle': isVehicleHistory }">
    <VehicleHeader
      v-if="isVehicleHistory"
      :vehicle="vehicle"
      :cover="vehicleCover"
      :loading="vehicleLoading"
      :status="vehicleStatus"
      :tags="vehicleTags"
      @edit="openVehicleEdit"
    />
    <header v-else class="section-header">
      <div>
        <h1>{{ headerTitle }}</h1>
        <p class="muted">
          {{ headerSubtitle }}
        </p>
      </div>
      <div v-if="isHomeSection" class="header-actions">
        <button class="primary" type="button" @click="openHomeModal('ONE_TIME')">
          {{ isTaxSection ? "+ Nuevo impuesto" : "+ Nuevo" }}
        </button>
      </div>
    </header>

    <VehicleKpis
      v-if="isVehicleHistory"
      :kpis="vehicleKpis"
      :loading="loading || documentsLoading"
    />

    <div v-if="isVehicleHistory" class="section-search">
      <div class="search-field">
        <span class="search-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" role="img" focusable="false">
            <path
              d="M10.5 3a7.5 7.5 0 105.3 12.8l4.45 4.46a1 1 0 001.42-1.42l-4.46-4.45A7.5 7.5 0 0010.5 3zm0 2a5.5 5.5 0 110 11 5.5 5.5 0 010-11z"
              fill="currentColor"
            />
          </svg>
        </span>
        <input
          v-model="searchTerm"
          type="search"
          placeholder="Buscar en historial, próximos y documentos..."
          aria-label="Buscar en el detalle del vehículo"
        />
        <button
          v-if="searchTerm"
          class="search-clear"
          type="button"
          aria-label="Limpiar búsqueda"
          @click="searchTerm = ''"
        >
          ×
        </button>
      </div>
      <button class="primary" type="button" @click="openModal">
        + Programar
      </button>
    </div>

    <MaintenanceTimeline
      v-if="showMaintenance"
      :items="timelineUpcoming"
      :loading="loading"
      :show-action="false"
      @create="openModal"
      @open="handleRecordClick"
    />
    <section v-if="showMaintenance" class="recurring-section">
      <div class="section-row">
        <div>
          <h2>{{ recurringTitle }}</h2>
          <p class="muted">{{ recurringSubtitle }}</p>
        </div>
        <button
          v-if="!isHomeSection"
          class="primary"
          type="button"
          @click="openRecurringModal()"
        >
          + Nuevo recurrente
        </button>
      </div>
      <section v-if="recurringError" class="state error">{{ recurringError }}</section>
      <section v-else-if="recurringLoading" class="state">Cargando recurrencias...</section>
      <div v-else-if="!filteredRecurring.length" class="empty-card">
        No hay mantenimientos recurrentes todavía.
      </div>
      <div v-else class="records-grid">
        <article v-for="item in filteredRecurring" :key="item.id" class="record-card">
          <div class="record-head">
            <div>
              <h3>{{ item.typeName || item.name }}</h3>
              <p class="muted small">{{ recurrenceLabel(item) }}</p>
            </div>
            <span class="chip" :class="urgencyTone(daysUntil(item.nextDueAt))">
              {{ item.nextDueAt ? timeRemaining(item.nextDueAt) : "Sin fecha" }}
            </span>
          </div>
          <div class="record-meta">
            <span>Última vez: {{ item.lastPerformedAt ? formatDate(item.lastPerformedAt) : "—" }}</span>
            <span>Próxima: {{ item.nextDueAt ? formatDate(item.nextDueAt) : "—" }}</span>
          </div>
          <p v-if="item.notes" class="muted small">{{ item.notes }}</p>
          <div class="record-actions">
            <button class="ghost" type="button" @click="openRecurringModal(item)">Editar</button>
            <button class="ghost danger" type="button" @click="removeRecurring(item)">Eliminar</button>
          </div>
        </article>
      </div>
    </section>
    <section v-if="showMaintenance && error" class="state error">{{ error }}</section>

    <DocumentsGrid
      v-if="showDocuments"
      :documents="documentsForGrid"
      :subtitle="documentsSubtitle"
      :loading="documentsLoading"
      :error="documentsError"
      :has-search="hasSearch"
      @create="openDocumentModal"
      @open="openDocumentModal"
      @preview="openLightbox"
    />

    <HistoryTimeline
      v-if="showMaintenance"
      :records="historyTimelineRecords"
      :loading="loading"
      :clickable="isVehicleHistory || isHomeSection"
      @open="handleRecordClick"
    />

    <section v-if="isVehicleHistory" class="vehicle-danger">
      <div class="danger-card">
        <div>
          <h2>Eliminar vehículo</h2>
          <p class="muted">
            El vehículo se eliminará de tu lista, pero los datos quedarán guardados.
          </p>
        </div>
        <button class="ghost danger" :disabled="deletingVehicle" @click="removeVehicle">
          {{ deletingVehicle ? "Eliminando..." : "Eliminar vehículo" }}
        </button>
      </div>
      <p v-if="vehicleDeleteError" class="state error">{{ vehicleDeleteError }}</p>
    </section>

    <input ref="ticketInput" type="file" accept="image/*" multiple hidden @change="uploadTickets" />

    <div v-if="showModal && !isHomeSection" class="modal-backdrop" @click.self="closeModal()">
      <div class="modal">
        <header>
          <h3>{{ form.id ? "Editar mantenimiento" : "Nuevo mantenimiento" }}</h3>
          <button class="icon danger" @click="closeModal()">×</button>
        </header>
        <div class="modal-body">
          <label>
            Tipo de mantenimiento
            <select v-model="form.typeId">
              <option disabled value="">Selecciona un tipo</option>
              <option v-for="type in typesForCategory" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </label>
          <div class="type-actions">
            <button class="ghost small" type="button" @click="toggleNewTypeForm">
              {{ showNewTypeForm ? "Cancelar" : "Añadir tipo" }}
            </button>
          </div>
          <div v-if="showNewTypeForm" class="row type-new">
            <label>
              Nuevo tipo
              <input v-model="newTypeName" type="text" placeholder="Ej: Cambio aceite" />
            </label>
            <button class="ghost" type="button" :disabled="!newTypeName.trim()" @click="createType">
              Crear
            </button>
          </div>
          <label>
            Descripción
            <textarea v-model="form.description" rows="3" placeholder="Detalles opcionales"></textarea>
          </label>
          <div class="row">
            <label>
              Fecha de realización
              <input v-model="form.performedAt" type="date" />
            </label>
            <label>
              Gasto EUR
              <input v-model.number="form.costEur" type="number" step="0.01" />
              <small class="muted">Se registrará como gasto en la sección correspondiente.</small>
            </label>
          </div>
          <label>
            Tickets (opcional)
            <label
              class="modal-dropzone"
              :class="{ 'is-active': ticketDragActive }"
              role="button"
              tabindex="0"
              aria-label="Arrastra tickets o pulsa para subir archivos"
              @keydown.enter.prevent="openTicketFileDialog"
              @keydown.space.prevent="openTicketFileDialog"
              @dragover.prevent="onTicketDragOver"
              @dragleave.prevent="onTicketDragLeave"
              @drop.prevent="onTicketDrop"
            >
              <div class="modal-dropzone__icon" aria-hidden="true">UP</div>
              <div class="modal-dropzone__text">
                <p>Arrastra archivos aquí o <span>haz clic para subir</span></p>
                <small>Imágenes de tickets en JPG o PNG.</small>
              </div>
              <input
                ref="modalFiles"
                type="file"
                accept="image/*"
                multiple
                class="sr-only"
                @change="onModalFilesSelected"
              />
            </label>
          </label>
          <div v-if="selectedTicketFiles.length" class="ticket-preview">
            <p class="muted">Vista previa</p>
            <div class="ticket-preview-grid">
              <div
                v-for="(ticket, index) in selectedTicketFiles"
                :key="ticket.id"
                class="ticket-preview-item"
              >
                <img
                  :src="ticket.url"
                  :alt="ticket.name"
                  @click="openLightbox(ticket.url, ticket.name)"
                />
                <button class="ticket-remove" @click.stop="removePendingTicket(index)">×</button>
              </div>
            </div>
          </div>
          <div v-if="existingTickets.length" class="ticket-preview">
            <p class="muted">Tickets existentes</p>
            <div class="ticket-preview-grid">
              <div
                v-for="ticket in existingTickets"
                :key="ticket.id"
                class="ticket-preview-item"
              >
                <img
                  :src="ticket.data"
                  :alt="ticket.name"
                  @click="openLightbox(ticket.data, ticket.name)"
                />
                <button
                  class="ticket-remove"
                  @click.stop="removeExistingTicket(ticket.id)"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
          <p v-if="formError" class="form-error">{{ formError }}</p>
        </div>
        <footer>
          <button class="ghost" :disabled="saving" @click="closeModal()">Cancelar</button>
          <button
            v-if="form.id"
            class="ghost danger"
            :disabled="saving"
            @click="removeRecordFromModal"
          >
            Eliminar
          </button>
          <button class="primary" :disabled="saving" @click="saveRecord">
            <span v-if="saving" class="btn-spinner" aria-hidden="true"></span>
            {{ saving ? "Guardando..." : form.id ? "Guardar" : "Crear" }}
          </button>
        </footer>
      </div>
    </div>

    <div v-if="showRecurringModal && !isHomeSection" class="modal-backdrop" @click.self="closeRecurringModal()">
      <div class="modal">
        <header>
          <h3>{{ recurringForm.id ? "Editar recurrencia" : "Nueva recurrencia" }}</h3>
          <button class="icon danger" @click="closeRecurringModal()">×</button>
        </header>
        <div class="modal-body">
          <label>
            Tipo de mantenimiento
            <select v-model="recurringForm.typeId">
              <option disabled value="">Selecciona un tipo</option>
              <option v-for="type in typesForCategory" :key="type.id" :value="type.id">
                {{ type.name }}
              </option>
            </select>
          </label>
          <div class="type-actions">
            <button class="ghost small" type="button" @click="toggleNewTypeForm">
              {{ showNewTypeForm ? "Cancelar" : "Añadir tipo" }}
            </button>
          </div>
          <div v-if="showNewTypeForm" class="row type-new">
            <label>
              Nuevo tipo
              <input v-model="newTypeName" type="text" placeholder="Ej: Cambio aceite" />
            </label>
            <button class="ghost" type="button" :disabled="!newTypeName.trim()" @click="createType">
              Crear
            </button>
          </div>
          <div class="row">
            <label>
              Cada
              <input v-model.number="recurringForm.recurrenceValue" type="number" min="1" />
            </label>
            <label>
              Unidad
              <select v-model="recurringForm.recurrenceUnit">
                <option value="DAYS">Días</option>
                <option value="WEEKS">Semanas</option>
                <option value="MONTHS">Meses</option>
                <option value="YEARS">Años</option>
              </select>
            </label>
          </div>
          <label>
            Notas (opcional)
            <textarea v-model="recurringForm.notes" rows="3" placeholder="Recordatorios o detalles."></textarea>
          </label>
          <label v-if="supportsExpenseRecurring && !taxRequiresExpenseRecurring" class="checkbox line">
            <input type="checkbox" v-model="recurringForm.expenseEnabled" />
            <span></span>
            Añadir gasto recurrente asociado
          </label>
          <p v-else-if="taxRequiresExpenseRecurring" class="muted small">
            Los impuestos recurrentes se registran como gasto recurrente automáticamente.
          </p>
          <div v-if="supportsExpenseRecurring && recurringForm.expenseEnabled" class="expense-panel">
            <div class="row">
              <label>
                Nombre del gasto
                <input
                  v-model="recurringForm.expenseName"
                  type="text"
                  :readonly="recurringForm.expenseNameLocked"
                  :class="{ readonly: recurringForm.expenseNameLocked }"
                  placeholder="Ej: ITV, aceite, revisión"
                />
              </label>
              <label>
                Importe EUR
                <input v-model.number="recurringForm.expenseAmountEur" type="number" min="0" step="0.01" />
              </label>
            </div>
            <div class="row">
              <label>
                Fecha inicio
                <input v-model="recurringForm.expenseStartAt" type="date" />
              </label>
              <label>
                Moneda
                <input v-model="recurringForm.expenseCurrency" type="text" placeholder="EUR" />
              </label>
            </div>
            <label>
              Descripción (opcional)
              <textarea
                v-model="recurringForm.expenseDescription"
                rows="3"
                placeholder="Detalles del gasto recurrente"
              ></textarea>
            </label>
            <div class="row">
              <label>
                Proveedor (opcional)
                <select v-model="recurringForm.expenseProviderId">
                  <option :value="null">Sin proveedor</option>
                  <option v-for="provider in expenseProviders" :key="provider.id" :value="provider.id">
                    {{ provider.name }}
                  </option>
                </select>
              </label>
              <label>
                Tipología (opcional)
                <select v-model="recurringForm.expenseCategoryId">
                  <option :value="null">Sin tipología</option>
                  <option v-for="category in expenseCategories" :key="category.id" :value="category.id">
                    {{ category.name }}
                  </option>
                </select>
              </label>
            </div>
            <label class="checkbox line">
              <input type="checkbox" v-model="recurringForm.expenseActive" />
              <span></span>
              Gasto activo
            </label>
          </div>
          <label class="checkbox line">
            <input type="checkbox" v-model="recurringForm.active" />
            <span></span>
            Activo
          </label>
          <p v-if="recurringFormError" class="form-error">{{ recurringFormError }}</p>
        </div>
        <footer>
          <button class="ghost" :disabled="recurringSaving" @click="closeRecurringModal()">Cancelar</button>
          <button
            v-if="recurringForm.id"
            class="ghost danger"
            :disabled="recurringSaving"
            @click="removeRecurring(recurringForm)"
          >
            Eliminar
          </button>
          <button class="primary" :disabled="recurringSaving" @click="saveRecurring">
            <span v-if="recurringSaving" class="btn-spinner" aria-hidden="true"></span>
            {{ recurringSaving ? "Guardando..." : recurringForm.id ? "Guardar" : "Crear" }}
          </button>
        </footer>
      </div>
    </div>

    <div v-if="homeModalOpen" class="modal-backdrop" @click.self="closeHomeModal()">
      <div class="modal">
        <header>
          <h3>{{ homeModalTitle }}</h3>
          <button class="icon danger" @click="closeHomeModal()">×</button>
        </header>
        <div class="modal-body">
          <div class="modal-toggle">
            <button
              class="ghost"
              :class="{ active: homeModalMode === 'ONE_TIME' }"
              type="button"
              :disabled="saving || recurringSaving"
              @click="switchHomeMode('ONE_TIME')"
            >
              Puntual
            </button>
            <button
              class="ghost"
              :class="{ active: homeModalMode === 'RECURRING' }"
              type="button"
              :disabled="saving || recurringSaving"
              @click="switchHomeMode('RECURRING')"
            >
              Recurrente
            </button>
          </div>

          <template v-if="homeModalMode === 'ONE_TIME'">
            <label>
              {{ isTaxSection ? "Tipo de impuesto" : "Tipo de mantenimiento" }}
              <select v-model="form.typeId">
                <option disabled value="">Selecciona un tipo</option>
                <option v-for="type in typesForCategory" :key="type.id" :value="type.id">
                  {{ type.name }}
                </option>
              </select>
            </label>
            <div class="type-actions">
              <button class="ghost small" type="button" @click="toggleNewTypeForm">
                {{ showNewTypeForm ? "Cancelar" : "Añadir tipo" }}
              </button>
            </div>
            <div v-if="showNewTypeForm" class="row type-new">
              <label>
                Nuevo tipo
                <input v-model="newTypeName" type="text" placeholder="Ej: Cambio aceite" />
              </label>
              <button class="ghost" type="button" :disabled="!newTypeName.trim()" @click="createType">
                Crear
              </button>
            </div>
            <label>
              Descripción
              <textarea v-model="form.description" rows="3" placeholder="Detalles opcionales"></textarea>
            </label>
            <div v-if="isTaxSection" class="row">
              <label>
                Emisor del impuesto
                <input v-model="form.taxIssuer" type="text" placeholder="Ayuntamiento, Hacienda..." />
              </label>
              <label>
                Fecha de emisión
                <input v-model="form.taxIssueDate" type="date" />
              </label>
            </div>
            <div v-if="isTaxSection" class="row">
              <label>
                Fecha de pago
                <input v-model="form.taxPaymentDate" type="date" />
              </label>
            </div>
            <div class="row">
              <label>
                {{ isTaxSection ? "Fecha de registro" : "Fecha de realización" }}
                <input v-model="form.performedAt" type="date" />
              </label>
              <label>
                Gasto EUR
                <input v-model.number="form.costEur" type="number" step="0.01" />
                <small class="muted">Se registrará como gasto en la sección correspondiente.</small>
              </label>
            </div>
          <label>
            Tickets (opcional)
            <label
              class="modal-dropzone"
              :class="{ 'is-active': ticketDragActive }"
              role="button"
              tabindex="0"
              aria-label="Arrastra tickets o pulsa para subir archivos"
              @keydown.enter.prevent="openTicketFileDialog"
              @keydown.space.prevent="openTicketFileDialog"
              @dragover.prevent="onTicketDragOver"
              @dragleave.prevent="onTicketDragLeave"
              @drop.prevent="onTicketDrop"
            >
              <div class="modal-dropzone__icon" aria-hidden="true">UP</div>
              <div class="modal-dropzone__text">
                <p>Arrastra archivos aquí o <span>haz clic para subir</span></p>
                <small>Imágenes de tickets en JPG o PNG.</small>
              </div>
              <input
                ref="modalFiles"
                type="file"
                accept="image/*"
                multiple
                class="sr-only"
                @change="onModalFilesSelected"
              />
            </label>
          </label>
            <div v-if="selectedTicketFiles.length" class="ticket-preview">
              <p class="muted">Vista previa</p>
              <div class="ticket-preview-grid">
                <div
                  v-for="(ticket, index) in selectedTicketFiles"
                  :key="ticket.id"
                  class="ticket-preview-item"
                >
                  <img
                    :src="ticket.url"
                    :alt="ticket.name"
                    @click="openLightbox(ticket.url, ticket.name)"
                  />
                  <button class="ticket-remove" @click.stop="removePendingTicket(index)">×</button>
                </div>
              </div>
            </div>
            <div v-if="existingTickets.length" class="ticket-preview">
              <p class="muted">Tickets existentes</p>
              <div class="ticket-preview-grid">
                <div
                  v-for="ticket in existingTickets"
                  :key="ticket.id"
                  class="ticket-preview-item"
                >
                  <img
                    :src="ticket.data"
                    :alt="ticket.name"
                    @click="openLightbox(ticket.data, ticket.name)"
                  />
                  <button
                    class="ticket-remove"
                    @click.stop="removeExistingTicket(ticket.id)"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
            <p v-if="formError" class="form-error">{{ formError }}</p>
          </template>

          <template v-else>
            <label>
              {{ isTaxSection ? "Tipo de impuesto" : "Tipo de mantenimiento" }}
              <select v-model="recurringForm.typeId">
                <option disabled value="">Selecciona un tipo</option>
                <option v-for="type in typesForCategory" :key="type.id" :value="type.id">
                  {{ type.name }}
                </option>
              </select>
            </label>
            <div class="type-actions">
              <button class="ghost small" type="button" @click="toggleNewTypeForm">
                {{ showNewTypeForm ? "Cancelar" : "Añadir tipo" }}
              </button>
            </div>
            <div v-if="showNewTypeForm" class="row type-new">
              <label>
                Nuevo tipo
                <input v-model="newTypeName" type="text" placeholder="Ej: Cambio aceite" />
              </label>
              <button class="ghost" type="button" :disabled="!newTypeName.trim()" @click="createType">
                Crear
              </button>
            </div>
            <div class="row">
              <label>
                Cada
                <input v-model.number="recurringForm.recurrenceValue" type="number" min="1" />
              </label>
              <label>
                Unidad
                <select v-model="recurringForm.recurrenceUnit">
                  <option value="DAYS">Días</option>
                  <option value="WEEKS">Semanas</option>
                  <option value="MONTHS">Meses</option>
                  <option value="YEARS">Años</option>
                </select>
              </label>
            </div>
            <label>
              Notas (opcional)
              <textarea v-model="recurringForm.notes" rows="3" placeholder="Recordatorios o detalles."></textarea>
            </label>
            <label v-if="supportsExpenseRecurring && !taxRequiresExpenseRecurring" class="checkbox line">
              <input type="checkbox" v-model="recurringForm.expenseEnabled" />
              <span></span>
              Añadir gasto recurrente asociado
            </label>
            <p v-else-if="taxRequiresExpenseRecurring" class="muted small">
              Los impuestos recurrentes se registran como gasto recurrente automáticamente.
            </p>
            <div v-if="supportsExpenseRecurring && recurringForm.expenseEnabled" class="expense-panel">
              <div class="row">
                <label>
                  Nombre del gasto
                  <input
                    v-model="recurringForm.expenseName"
                    type="text"
                    :readonly="recurringForm.expenseNameLocked"
                    :class="{ readonly: recurringForm.expenseNameLocked }"
                    placeholder="Ej: ITV, aceite, revisión"
                  />
                </label>
                <label>
                  Importe EUR
                  <input v-model.number="recurringForm.expenseAmountEur" type="number" min="0" step="0.01" />
                </label>
              </div>
              <div class="row">
                <label>
                  Fecha inicio
                  <input v-model="recurringForm.expenseStartAt" type="date" />
                </label>
                <label>
                  Moneda
                  <input v-model="recurringForm.expenseCurrency" type="text" placeholder="EUR" />
                </label>
              </div>
              <label>
                Descripción (opcional)
                <textarea
                  v-model="recurringForm.expenseDescription"
                  rows="3"
                  placeholder="Detalles del gasto recurrente"
                ></textarea>
              </label>
              <div class="row">
                <label>
                  Proveedor (opcional)
                  <select v-model="recurringForm.expenseProviderId">
                    <option :value="null">Sin proveedor</option>
                    <option v-for="provider in expenseProviders" :key="provider.id" :value="provider.id">
                      {{ provider.name }}
                    </option>
                  </select>
                </label>
                <label>
                  Tipología (opcional)
                  <select v-model="recurringForm.expenseCategoryId">
                    <option :value="null">Sin tipología</option>
                    <option v-for="category in expenseCategories" :key="category.id" :value="category.id">
                      {{ category.name }}
                    </option>
                  </select>
                </label>
              </div>
              <label class="checkbox line">
                <input type="checkbox" v-model="recurringForm.expenseActive" />
                <span></span>
                Gasto activo
              </label>
            </div>
            <label class="checkbox line">
              <input type="checkbox" v-model="recurringForm.active" />
              <span></span>
              Activo
            </label>
            <p v-if="recurringFormError" class="form-error">{{ recurringFormError }}</p>
          </template>
        </div>
        <footer>
          <button class="ghost" :disabled="saving || recurringSaving" @click="closeHomeModal()">Cancelar</button>
          <button
            v-if="homeModalMode === 'ONE_TIME' && form.id"
            class="ghost danger"
            :disabled="saving"
            @click="removeRecordFromModal"
          >
            Eliminar
          </button>
          <button
            v-if="homeModalMode === 'RECURRING' && recurringForm.id"
            class="ghost danger"
            :disabled="recurringSaving"
            @click="removeRecurring(recurringForm)"
          >
            Eliminar
          </button>
          <button
            class="primary"
            :disabled="homeModalMode === 'ONE_TIME' ? saving : recurringSaving"
            @click="homeModalMode === 'ONE_TIME' ? saveRecord() : saveRecurring()"
          >
            <span
              v-if="homeModalMode === 'ONE_TIME' ? saving : recurringSaving"
              class="btn-spinner"
              aria-hidden="true"
            ></span>
            {{
              homeModalMode === "ONE_TIME"
                ? saving
                  ? "Guardando..."
                  : form.id
                    ? "Guardar"
                    : "Crear"
                : recurringSaving
                  ? "Guardando..."
                  : recurringForm.id
                    ? "Guardar"
                    : "Crear"
            }}
          </button>
        </footer>
      </div>
    </div>

    <div v-if="showDocumentModal" class="modal-backdrop" @click.self="closeDocumentModal()">
      <div class="modal" :class="{ 'is-blocked': documentSaving }">
        <header>
          <h3>{{ documentForm.id ? "Editar documento" : "Nuevo documento" }}</h3>
          <button class="icon danger" :disabled="documentSaving" @click="closeDocumentModal()">×</button>
        </header>
        <div class="modal-body" :class="{ 'is-disabled': documentSaving }">
          <label>
            Nombre
            <input
              v-model="documentForm.name"
              type="text"
              placeholder="Seguro, ITV, ficha técnica..."
              :disabled="documentSaving"
            />
          </label>
          <label>
            Descripción
            <textarea
              v-model="documentForm.description"
              rows="3"
              placeholder="Detalles opcionales"
              :disabled="documentSaving"
            ></textarea>
          </label>
          <div class="row">
            <label>
              Fecha de subida
              <input v-model="documentForm.uploadedAt" type="date" :disabled="documentSaving" />
            </label>
            <label>
              Fecha de caducidad
              <input v-model="documentForm.expiresAt" type="date" :disabled="documentSaving" />
            </label>
          </div>
          <label>
            Archivos
            <label
              class="modal-dropzone"
              :class="{ 'is-active': documentDragActive }"
              role="button"
              tabindex="0"
              aria-label="Arrastra documentos o pulsa para subir archivos"
              @keydown.enter.prevent="openDocumentFileDialog"
              @keydown.space.prevent="openDocumentFileDialog"
              @dragover.prevent="onDocumentDragOver"
              @dragleave.prevent="onDocumentDragLeave"
              @drop.prevent="onDocumentDrop"
            >
              <div class="modal-dropzone__icon" aria-hidden="true">UP</div>
              <div class="modal-dropzone__text">
                <p>Arrastra archivos aquí o <span>haz clic para subir</span></p>
                <small>PDF o imágenes escaneadas.</small>
              </div>
              <input
                ref="documentFilesInput"
                type="file"
                multiple
                accept="image/*,application/pdf"
                class="sr-only"
                @change="onDocumentFilesSelected"
              />
            </label>
          </label>
          <div v-if="selectedDocumentFiles.length" class="ticket-preview">
            <p class="muted">Archivos nuevos</p>
            <div class="ticket-preview-grid">
              <div
                v-for="(file, index) in selectedDocumentFiles"
                :key="file.id"
                class="ticket-preview-item"
              >
                <img
                  v-if="file.preview"
                  :src="file.preview"
                  :alt="file.name"
                  @click="openLightbox(file.preview, file.name)"
                />
                <div v-else class="file-fallback">{{ file.name }}</div>
                <button class="ticket-remove" @click.stop="removePendingDocumentFile(index)">×</button>
              </div>
            </div>
          </div>
          <div v-if="documentFiles.length" class="ticket-preview">
            <p class="muted">Archivos existentes</p>
            <div class="ticket-preview-grid">
              <div
                v-for="file in documentFiles"
                :key="file.id"
                class="ticket-preview-item"
              >
                <img
                  v-if="file.type === 'IMAGE'"
                  :src="file.data"
                  :alt="file.name"
                  @click="openLightbox(file.data, file.name)"
                />
                <div v-else class="file-fallback">{{ file.name }}</div>
                <button
                  class="ticket-remove"
                  @click.stop="removeExistingDocumentFile(documentForm.id, file.id)"
                >
                  ×
                </button>
              </div>
            </div>
          </div>
          <p v-if="documentSaving" class="muted">
            <span class="btn-spinner" aria-hidden="true"></span>
            Guardando documento...
          </p>
          <p v-if="documentFormError" class="form-error">{{ documentFormError }}</p>
        </div>
        <footer>
          <button class="ghost" :disabled="documentSaving" @click="closeDocumentModal()">Cancelar</button>
          <button
            v-if="documentForm.id"
            class="ghost danger"
            :disabled="documentSaving"
            @click="removeDocumentFromModal"
          >
            Eliminar
          </button>
          <button class="primary" :disabled="documentSaving" @click="saveDocument">
            <span v-if="documentSaving" class="btn-spinner" aria-hidden="true"></span>
            {{ documentSaving ? "Guardando..." : documentForm.id ? "Guardar" : "Crear" }}
          </button>
        </footer>
        <div v-if="documentSaving" class="modal-blocker" aria-hidden="true"></div>
      </div>
    </div>

    <div v-if="showVehicleModal" class="modal-backdrop" @click.self="closeVehicleEdit()">
      <div class="modal">
        <header>
          <h3>Editar vehículo</h3>
          <button class="icon danger" @click="closeVehicleEdit()">×</button>
        </header>
        <div class="modal-body">
          <div class="row">
            <label>
              Tipo
              <select v-model="vehicleForm.type">
                <option v-for="type in vehicleTypes" :key="type" :value="type">
                  {{ typeLabel(type) }}
                </option>
              </select>
            </label>
            <label>
              Año de matriculación
              <input v-model.number="vehicleForm.registrationYear" type="number" min="1900" max="2100" />
            </label>
          </div>
          <label>
            Marca
            <input v-model="vehicleForm.brand" type="text" placeholder="Seat, Yamaha..." />
          </label>
          <label>
            Modelo
            <input v-model="vehicleForm.model" type="text" placeholder="Ibiza, MT-07..." />
          </label>
          <div class="row">
            <label>
              Matrícula
              <input v-model="vehicleForm.plate" type="text" placeholder="0000-ABC" />
            </label>
            <label>
              Número de bastidor
              <input v-model="vehicleForm.vin" type="text" placeholder="VIN" />
            </label>
          </div>
          <label>
            Fotos
            <div
              class="photo-dropzone"
              role="button"
              tabindex="0"
              @click="openVehiclePhotoDialog"
              @keydown.enter.prevent="openVehiclePhotoDialog"
              @keydown.space.prevent="openVehiclePhotoDialog"
            >
              <span class="photo-dropzone__plus">+</span>
              <span class="photo-dropzone__text">Subir fotos</span>
            </div>
            <input
              ref="vehiclePhotoInput"
              type="file"
              accept="image/*"
              multiple
              class="sr-only"
              @change="onVehiclePhotosSelected"
            />
          </label>
          <div v-if="vehicleSelectedPhotos.length" class="ticket-preview">
            <p class="muted">Fotos nuevas</p>
            <div class="ticket-preview-grid">
              <div
                v-for="(photo, index) in vehicleSelectedPhotos"
                :key="photo.id"
                class="ticket-preview-item"
              >
                <img
                  :src="photo.preview"
                  :alt="photo.name"
                  @click="openLightbox([{ src: photo.preview, alt: photo.name }], 0, photo.name)"
                />
                <button class="ticket-remove" @click.stop="removeVehiclePendingPhoto(index)">×</button>
              </div>
            </div>
          </div>
          <div v-if="vehicleExistingPhotos.length" class="ticket-preview">
            <p class="muted">Fotos existentes</p>
            <div class="ticket-preview-grid">
              <div
                v-for="photo in vehicleExistingPhotos"
                :key="photo.id"
                class="ticket-preview-item"
              >
                <img
                  :src="photo.data"
                  :alt="photo.name"
                  @click="openLightbox([{ src: photo.data, alt: photo.name }], 0, photo.name)"
                />
                <button class="ticket-remove" @click.stop="removeVehicleExistingPhoto(photo.id)">×</button>
              </div>
            </div>
          </div>
          <p v-if="vehicleFormError" class="form-error">{{ vehicleFormError }}</p>
        </div>
        <footer>
          <button class="ghost" :disabled="vehicleSaving" @click="closeVehicleEdit()">Cancelar</button>
          <button class="primary" :disabled="vehicleSaving" @click="saveVehicleEdit">
            {{ vehicleSaving ? "Guardando..." : "Guardar" }}
          </button>
        </footer>
      </div>
    </div>

    <div v-if="lightboxImages.length" class="lightbox" @click.self="closeLightbox">
      <div class="lightbox-content" role="dialog" aria-modal="true">
      <button class="lightbox-close" @click="closeLightbox">×</button>
        <button
          v-if="lightboxImages.length > 1"
          class="lightbox-nav prev"
          type="button"
          aria-label="Imagen anterior"
          @click="prevLightboxImage"
        >
          ‹
        </button>
        <div
          class="lightbox-media"
          @touchstart="onLightboxTouchStart"
          @touchend="onLightboxTouchEnd"
        >
          <img :src="currentLightboxImage?.src" :alt="currentLightboxImage?.alt || lightboxAlt" />
        </div>
        <button
          v-if="lightboxImages.length > 1"
          class="lightbox-nav next"
          type="button"
          aria-label="Imagen siguiente"
          @click="nextLightboxImage"
        >
          ›
        </button>
        <div v-if="lightboxImages.length > 1" class="lightbox-dots">
          <button
            v-for="(img, idx) in lightboxImages"
            :key="`dot-${idx}`"
            type="button"
            class="lightbox-dot"
            :class="{ active: idx === lightboxIndex }"
            @click="setLightboxIndex(idx)"
            :aria-label="`Ir a imagen ${idx + 1}`"
          ></button>
        </div>
        <div v-if="lightboxImages.length > 1" class="lightbox-thumbs">
          <button
            v-for="(img, idx) in lightboxImages"
            :key="`thumb-${idx}`"
            type="button"
            class="lightbox-thumb"
            :class="{ active: idx === lightboxIndex }"
            @click="setLightboxIndex(idx)"
          >
            <img :src="img.src" :alt="img.alt || 'Imagen'" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchMaintenance,
  fetchUpcomingMaintenance,
  createMaintenance,
  updateMaintenance,
  deleteMaintenance,
  uploadMaintenanceTickets,
  deleteMaintenanceTicket,
  fetchMaintenanceTypes,
  createMaintenanceType,
  createRecurringMaintenance,
  updateRecurringMaintenance,
  deleteRecurringMaintenance,
} from "../services/maintenance";
import { fetchVehicle, updateVehicle, deleteVehicle, deleteVehiclePhoto } from "../services/vehicles";
import {
  fetchRecurringExpenses,
  fetchExpenseProviders,
  fetchExpenseCategories,
} from "../services/expenses";
import {
  fetchDocuments,
  createDocument,
  updateDocument,
  deleteDocument,
  uploadDocumentFiles,
  deleteDocumentFile,
} from "../services/documents";
import VehicleHeader from "../components/vehicle/VehicleHeader.vue";
import VehicleKpis from "../components/vehicle/VehicleKpis.vue";
import DocumentsGrid from "../components/vehicle/DocumentsGrid.vue";
import MaintenanceTimeline from "../components/vehicle/MaintenanceTimeline.vue";
import HistoryTimeline from "../components/vehicle/HistoryTimeline.vue";

export default {
  name: "MaintenanceView",
  components: {
    VehicleHeader,
    VehicleKpis,
    DocumentsGrid,
    MaintenanceTimeline,
    HistoryTimeline,
  },
  inject: ["setVehicleBreadcrumb", "clearVehicleBreadcrumb"],
  props: {
    category: { type: String, required: true },
    title: { type: String, required: true },
    vehicleId: { type: [String, Number], default: null },
  },
  data() {
    return {
      records: [],
      upcoming: [],
      recurring: [],
      recurringLoading: false,
      recurringError: "",
      loading: false,
      error: "",
      formError: "",
      saving: false,
      showModal: false,
      form: this.emptyForm(),
      ticketUploadId: null,
      selectedTicketFiles: [],
      types: [],
      typesLoading: false,
      typesError: "",
      newTypeName: "",
      showNewTypeForm: false,
      documents: [],
      documentsLoading: false,
      documentsError: "",
      homeModalOpen: false,
      homeModalMode: "ONE_TIME",
      showDocumentModal: false,
      documentForm: this.emptyDocumentForm(),
      documentFormError: "",
      documentSaving: false,
      selectedDocumentFiles: [],
      documentFiles: [],
      ticketDragActive: false,
      documentDragActive: false,
      existingTickets: [],
      showRecurringModal: false,
      recurringForm: this.emptyRecurringForm(),
      recurringSaving: false,
      recurringFormError: "",
      searchTerm: "",
      vehicle: null,
      vehicleLoading: false,
      deletingVehicle: false,
      vehicleDeleteError: "",
      showVehicleModal: false,
      vehicleSaving: false,
      vehicleFormError: "",
      vehicleForm: this.emptyVehicleForm(),
      vehicleSelectedPhotos: [],
      vehicleExistingPhotos: [],
      vehicleTypes: ["CAR", "MOTORCYCLE", "VAN", "TRUCK", "OTHER"],
      lightboxImages: [],
      lightboxIndex: 0,
      lightboxAlt: "",
      lightboxTouchStartX: 0,
      expenseProviders: [],
      expenseCategories: [],
      expenseMetaLoading: false,
      expenseMetaError: "",
      expenseRecurringById: {},
    };
  },
  async mounted() {
    await this.loadAll();
    await this.loadTypes();
    await this.loadDocuments();
    await this.loadVehicle();
    await this.loadExpenseMetadata();
  },
  watch: {
    category() {
      this.showModal = false;
      this.formError = "";
      this.error = "";
      this.searchTerm = "";
      this.vehicleDeleteError = "";
      this.loadAll();
      this.loadTypes();
      this.loadDocuments();
      this.loadExpenseMetadata();
    },
    vehicleId() {
      this.searchTerm = "";
      this.vehicleDeleteError = "";
      this.loadAll();
      this.loadDocuments();
      this.loadVehicle();
      this.loadExpenseMetadata();
    },
    "recurringForm.typeId"() {
      this.updateExpenseNameLocked();
      this.updateTaxExpenseName();
    },
  },
  computed: {
    showDocuments() {
      if (this.category === "VEHICLE") {
        return Boolean(this.resolvedVehicleId);
      }
      return this.category === "HOME" || this.category === "PERSONAL";
    },
    showMaintenance() {
      return this.category !== "PERSONAL";
    },
    isHomeSection() {
      return this.category === "HOME" || this.category === "TAX";
    },
    isTaxSection() {
      return this.category === "TAX";
    },
    supportsExpenseRecurring() {
      return this.category !== "PERSONAL";
    },
    taxRequiresExpenseRecurring() {
      return this.isTaxSection;
    },
    isVehicleHistory() {
      return this.category === "VEHICLE";
    },
    hasSearch() {
      return Boolean(this.searchQuery);
    },
    searchQuery() {
      if (!this.isVehicleHistory) return "";
      return this.searchTerm.trim().toLowerCase();
    },
    filteredRecords() {
      return this.filterMaintenanceItems(this.records);
    },
    filteredUpcoming() {
      return this.filterMaintenanceItems(this.upcoming);
    },
    filteredRecurring() {
      return this.filterMaintenanceItems(this.recurring);
    },
    typesForCategory() {
      if (!Array.isArray(this.types)) return [];
      return this.types.filter((type) => type.category === this.category);
    },
    filteredDocuments() {
      if (!this.showDocuments) return [];
      if (!this.searchQuery) return this.documents;
      return this.documents.filter((doc) => this.documentMatches(doc));
    },
    resolvedVehicleId() {
      if (!this.vehicleId && this.vehicleId !== 0) return null;
      const parsed = Number(this.vehicleId);
      return Number.isNaN(parsed) ? null : parsed;
    },
    headerTitle() {
      if (this.vehicle) {
        const label = `${this.vehicle.brand} ${this.vehicle.model}`.trim();
        return label || this.title;
      }
      if (this.category === "PERSONAL") {
        return "Documentos personales";
      }
      if (this.category === "TAX") {
        return "Impuestos";
      }
      return this.title;
    },
    headerSubtitle() {
      if (this.category === "PERSONAL") {
        return "Guarda DNI, pasaporte, carnet de conducir y otros documentos personales.";
      }
      if (this.category === "TAX") {
        return "Gestiona impuestos, fechas clave y pagos pendientes.";
      }
      return "Registra los mantenimientos realizados y controla los próximos avisos.";
    },
    homeModalTitle() {
      if (this.homeModalMode === "RECURRING") {
        if (this.isTaxSection) {
          return this.recurringForm.id ? "Editar impuesto recurrente" : "Nuevo impuesto recurrente";
        }
        return this.recurringForm.id ? "Editar recurrencia" : "Nueva recurrencia";
      }
      if (this.isTaxSection) {
        return this.form.id ? "Editar impuesto" : "Nuevo impuesto";
      }
      return this.form.id ? "Editar mantenimiento" : "Nuevo mantenimiento";
    },
    documentsSubtitle() {
      if (this.category === "PERSONAL") {
        return "Sube y organiza tus documentos personales con fechas clave.";
      }
      if (this.category === "HOME") {
        return "Archivos relevantes del hogar y sus fechas clave.";
      }
      if (this.category === "VEHICLE") {
        return "Seguro, ITV, ficha técnica y documentos clave.";
      }
      return "";
    },
    recurringTitle() {
      if (this.category === "TAX") return "Impuestos recurrentes";
      return "Mantenimientos recurrentes";
    },
    recurringSubtitle() {
      if (this.category === "TAX") return "Planifica pagos periódicos y crea los gastos asociados.";
      return "Planifica tareas periódicas y recibe avisos automáticos.";
    },
    vehicleCover() {
      return this.vehicle?.photos?.[0]?.data || "";
    },
    vehicleStatus() {
      const alerts = this.computeAlertSummary();
      if (alerts.critical > 0) {
        return {
          label: "Atención",
          detail: `${alerts.critical} avisos críticos`,
          tone: "danger",
        };
      }
      if (alerts.upcoming > 0) {
        return {
          label: "Por revisar",
          detail: `${alerts.upcoming} avisos próximos`,
          tone: "warning",
        };
      }
      return {
        label: "En regla",
        detail: "Sin alertas próximas",
        tone: "success",
      };
    },
    typeLabel() {
      return (type) => {
        const map = {
          CAR: "Coche",
          MOTORCYCLE: "Moto",
          VAN: "Furgoneta",
          TRUCK: "Camión",
          OTHER: "Otro",
        };
        return map[type] || type;
      };
    },
    vehicleTags() {
      const tags = [];
      const itvDate = this.findNextItv();
      if (itvDate) {
        const days = this.daysUntil(itvDate);
        tags.push({
          label: `ITV ${this.formatDate(itvDate)}`,
          tone: days != null && days <= 30 ? "warning" : "info",
        });
      }
      tags.push({
        label: `${this.documents.length} docs`,
        tone: "neutral",
      });
      const alerts = this.computeAlertSummary();
      if (alerts.total > 0) {
        tags.push({
          label: `${alerts.total} alertas`,
          tone: alerts.critical > 0 ? "danger" : "warning",
        });
      }
      return tags;
    },
    vehicleKpis() {
      const annualSpend = this.computeAnnualSpend();
      const nextItv = this.findNextItv();
      const nextMaintenance = this.findNextMaintenance();
      const alerts = this.computeAlertSummary();
      const itvDays = nextItv ? this.daysUntil(nextItv) : null;
      const maintenanceDays = nextMaintenance ? this.daysUntil(nextMaintenance) : null;
      return [
        {
          label: "Gasto anual",
          value: this.formatEur(annualSpend),
          helper: "Últimos 12 meses",
          tone: "info",
        },
        {
          label: "Próxima ITV",
          value: nextItv ? this.formatDate(nextItv) : "—",
          helper: nextItv ? this.timeRemaining(nextItv) : "Sin dato",
          tone: itvDays != null && itvDays <= 30 ? "warning" : "success",
        },
        {
          label: "Próximo mantenimiento",
          value: nextMaintenance ? this.formatDate(nextMaintenance) : "—",
          helper: nextMaintenance ? this.timeRemaining(nextMaintenance) : "Sin programar",
          tone: maintenanceDays != null && maintenanceDays <= 14 ? "warning" : "info",
        },
        {
          label: "Nº documentos",
          value: String(this.documents.length),
          helper: "Archivos activos",
          tone: "info",
        },
        {
          label: "Alertas",
          value: String(alerts.total),
          helper: alerts.total ? "Revisar pendientes" : "Todo al día",
          tone: alerts.total ? "danger" : "success",
        },
      ];
    },
    timelineUpcoming() {
      return [...this.filteredUpcoming]
        .filter((item) => item.nextDueAt)
        .sort((a, b) => new Date(a.nextDueAt) - new Date(b.nextDueAt))
        .map((item) => {
          const days = this.daysUntil(item.nextDueAt);
          const tone = this.urgencyTone(days);
          const badge =
            days != null && days < 0
              ? "Vencido"
              : days != null && days <= 7
                ? "Urgente"
                : days != null && days <= 21
                  ? "Próximo"
                  : "Programado";
          return {
            id: item.id,
            name: item.name,
            dateLabel: this.formatDate(item.nextDueAt),
            timeRemaining: this.timeRemaining(item.nextDueAt),
            tone,
            badge,
            raw: item,
          };
        });
    },
    documentsForGrid() {
      return this.filteredDocuments.map((doc) => {
        const files = Array.isArray(doc.files) ? doc.files : [];
        const imageFiles = files.filter((file) => file.type === "IMAGE");
        const preview = imageFiles[0]?.data || "";
        const days = doc.expiresAt ? this.daysUntil(doc.expiresAt) : null;
        let statusLabel = "Sin caducidad";
        let statusTone = "neutral";
        if (doc.expiresAt) {
          if (days != null && days < 0) {
            statusLabel = "Caducado";
            statusTone = "danger";
          } else if (days != null && days <= 30) {
            statusLabel = "Próximo a caducar";
            statusTone = "warning";
          } else {
            statusLabel = "En regla";
            statusTone = "success";
          }
        }
        return {
          ...doc,
          preview,
          fileCount: files.length,
          imageFiles: imageFiles.map((file) => ({
            src: file.data,
            alt: file.name || doc.name || "Documento",
          })),
          uploadedAtLabel: this.formatDate(doc.uploadedAt),
          expiresAtLabel: doc.expiresAt ? this.formatDate(doc.expiresAt) : "—",
          statusLabel,
          statusTone,
        };
      });
    },
    historyTimelineRecords() {
      return this.filteredRecords.map((item) => ({
        ...item,
        performedAtLabel: this.formatDate(item.performedAt),
        costLabel: this.formatEur(item.costEur),
      }));
    },
    currentLightboxImage() {
      if (!this.lightboxImages.length) return null;
      return this.lightboxImages[this.lightboxIndex] || this.lightboxImages[0];
    },
  },
  methods: {
    emptyForm() {
      return {
        id: null,
        typeId: "",
        name: "",
        description: "",
        taxIssuer: "",
        taxIssueDate: "",
        taxPaymentDate: "",
        performedAt: "",
        costEur: 0,
      };
    },
    emptyVehicleForm() {
      return {
        id: null,
        type: "CAR",
        registrationYear: "",
        brand: "",
        model: "",
        plate: "",
        vin: "",
      };
    },
    emptyRecurringForm() {
      return {
        id: null,
        typeId: "",
        recurrenceValue: 1,
        recurrenceUnit: "MONTHS",
        notes: "",
        active: true,
        expenseEnabled: false,
        expenseRecurringId: null,
        expenseName: "",
        expenseNameLocked: false,
        expenseDescription: "",
        expenseAmountEur: 0,
        expenseStartAt: new Date().toISOString().slice(0, 10),
        expenseCurrency: "EUR",
        expenseProviderId: null,
        expenseCategoryId: null,
        expenseActive: true,
      };
    },
    emptyDocumentForm() {
      return {
        id: null,
        name: "",
        description: "",
        uploadedAt: new Date().toISOString().slice(0, 10),
        expiresAt: "",
      };
    },
    computeAnnualSpend() {
      const now = new Date();
      const yearAgo = new Date();
      yearAgo.setFullYear(now.getFullYear() - 1);
      return (this.records || []).reduce((sum, item) => {
        if (!item?.performedAt) return sum;
        const date = new Date(item.performedAt);
        if (Number.isNaN(date.getTime())) return sum;
        return date >= yearAgo ? sum + Number(item.costEur || 0) : sum;
      }, 0);
    },
    findNextItv() {
      const docs = Array.isArray(this.documents) ? this.documents : [];
      const itvDocs = docs.filter((doc) => {
        const text = `${doc.name || ""} ${doc.description || ""}`.toLowerCase();
        return text.includes("itv");
      });
      const futureDates = itvDocs
        .map((doc) => doc.expiresAt || doc.uploadedAt)
        .filter(Boolean)
        .map((value) => new Date(value))
        .filter((date) => !Number.isNaN(date.getTime()))
        .sort((a, b) => a - b);
      const today = new Date();
      const next = futureDates.find((date) => date >= today);
      return next ? next.toISOString().slice(0, 10) : null;
    },
    findNextMaintenance() {
      const upcoming = Array.isArray(this.upcoming) ? this.upcoming : [];
      const dates = upcoming
        .map((item) => item.nextDueAt)
        .filter(Boolean)
        .map((value) => new Date(value))
        .filter((date) => !Number.isNaN(date.getTime()))
        .sort((a, b) => a - b);
      return dates.length ? dates[0].toISOString().slice(0, 10) : null;
    },
    computeAlertSummary() {
      const upcoming = Array.isArray(this.upcoming) ? this.upcoming : [];
      const docs = Array.isArray(this.documents) ? this.documents : [];
      let overdue = 0;
      let soon = 0;
      let expiring = 0;
      let expiredDocs = 0;
      upcoming.forEach((item) => {
        const days = this.daysUntil(item.nextDueAt);
        if (days == null) return;
        if (days < 0) overdue += 1;
        if (days >= 0 && days <= 14) soon += 1;
      });
      docs.forEach((doc) => {
        if (!doc.expiresAt) return;
        const days = this.daysUntil(doc.expiresAt);
        if (days == null) return;
        if (days < 0) expiredDocs += 1;
        if (days >= 0 && days <= 30) expiring += 1;
      });
      const critical = overdue + expiredDocs;
      const upcomingCount = soon + expiring;
      return {
        critical,
        upcoming: upcomingCount,
        total: critical + upcomingCount,
      };
    },
    daysUntil(value) {
      if (!value) return null;
      const target = new Date(value);
      if (Number.isNaN(target.getTime())) return null;
      const today = new Date();
      target.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    },
    urgencyTone(days) {
      if (days == null) return "neutral";
      if (days < 0) return "danger";
      if (days <= 7) return "danger";
      if (days <= 21) return "warning";
      if (days <= 45) return "info";
      return "success";
    },
    async loadAll() {
      if (!this.showMaintenance) {
        this.loading = false;
        this.error = "";
        this.records = [];
        this.upcoming = [];
        this.recurring = [];
        return;
      }
      this.loading = true;
      this.error = "";
      try {
        const [records, recurring] = await Promise.all([
          fetchMaintenance(this.category, this.resolvedVehicleId),
          fetchUpcomingMaintenance(this.category, this.resolvedVehicleId),
        ]);
        this.records = (records || []).map((item) => this.normalizeRecord(item));
        this.recurring = (recurring || []).map((item) => this.normalizeRecurring(item));
        this.upcoming = this.recurring.filter((item) => item.active !== false);
      } catch (err) {
        this.error = err?.message || "No se pudo cargar el historial.";
      } finally {
        this.loading = false;
      }
    },
    async loadTypes() {
      if (!this.showMaintenance) {
        this.types = [];
        return;
      }
      this.typesLoading = true;
      this.typesError = "";
      try {
        const types = await fetchMaintenanceTypes(this.category);
        this.types = Array.isArray(types) ? types : [];
      } catch (err) {
        this.typesError = err?.message || "No se pudieron cargar los tipos.";
        this.types = [];
      } finally {
        this.typesLoading = false;
      }
    },
    async loadRecurring() {
      if (!this.showMaintenance) {
        this.recurring = [];
        this.expenseRecurringById = {};
        return;
      }
      this.recurringLoading = true;
      this.recurringError = "";
      try {
        const recurring = await fetchUpcomingMaintenance(this.category, this.resolvedVehicleId);
        const expenseMap = await this.loadExpenseRecurringMap(recurring || []);
        this.expenseRecurringById = expenseMap;
        this.recurring = (recurring || []).map((item) => this.normalizeRecurring(item));
        this.upcoming = this.recurring.filter((item) => item.active !== false);
      } catch (err) {
        this.recurringError = err?.message || "No se pudieron cargar las recurrencias.";
        this.recurring = [];
        this.upcoming = [];
        this.expenseRecurringById = {};
      } finally {
        this.recurringLoading = false;
      }
    },
    async loadExpenseMetadata() {
      if (!this.showMaintenance) {
        this.expenseProviders = [];
        this.expenseCategories = [];
        return;
      }
      this.expenseMetaLoading = true;
      this.expenseMetaError = "";
      try {
        const [providers, categories] = await Promise.all([
          fetchExpenseProviders(),
          fetchExpenseCategories(),
        ]);
        this.expenseProviders = Array.isArray(providers) ? providers : [];
        this.expenseCategories = Array.isArray(categories) ? categories : [];
      } catch (err) {
        this.expenseMetaError = err?.message || "No se pudieron cargar los datos de gastos.";
        this.expenseProviders = [];
        this.expenseCategories = [];
      } finally {
        this.expenseMetaLoading = false;
      }
    },
    async loadExpenseRecurringMap(recurringItems) {
      const ids = (recurringItems || [])
        .map((item) => item.expenseRecurringId ?? item.expense_recurring_id)
        .filter((value) => value !== null && value !== undefined);
      if (!ids.length) return {};
      const section = this.category === "VEHICLE" ? "VEHICLE" : "HOME";
      try {
        const list = await fetchRecurringExpenses({
          section,
          vehicleId: this.category === "VEHICLE" ? this.resolvedVehicleId : null,
        });
        const map = {};
        (list || []).forEach((item) => {
          map[item.id] = item;
        });
        return map;
      } catch (_) {
        return {};
      }
    },
    async loadDocuments() {
      if (!this.showDocuments) {
        this.documents = [];
        this.documentsError = "";
        this.documentsLoading = false;
        return;
      }
      this.documentsLoading = true;
      this.documentsError = "";
      try {
        const docs = await fetchDocuments(this.category, this.resolvedVehicleId);
        this.documents = (docs || []).map((doc) => this.normalizeDocument(doc));
      } catch (err) {
        this.documentsError = err?.message || "No se pudieron cargar los documentos.";
      } finally {
        this.documentsLoading = false;
      }
    },
    async loadVehicle() {
      if (!this.resolvedVehicleId) {
        this.vehicle = null;
        this.vehicleLoading = false;
        this.clearVehicleBreadcrumb?.();
        return;
      }
      this.vehicleLoading = true;
      try {
        this.vehicle = await fetchVehicle(this.resolvedVehicleId);
        const label = `${this.vehicle?.brand || ""} ${this.vehicle?.model || ""}`.trim();
        if (label) {
          this.setVehicleBreadcrumb?.(label);
        } else {
          this.clearVehicleBreadcrumb?.();
        }
      } catch (_) {
        this.vehicle = null;
        this.clearVehicleBreadcrumb?.();
      } finally {
        this.vehicleLoading = false;
      }
    },
    openVehicleEdit() {
      if (!this.vehicle) return;
      this.vehicleFormError = "";
      this.resetPendingVehiclePhotos();
      if (this.$refs.vehiclePhotoInput) {
        this.$refs.vehiclePhotoInput.value = "";
      }
      this.vehicleForm = {
        id: this.vehicle.id,
        type: this.vehicle.type || "CAR",
        registrationYear: this.vehicle.registrationYear ?? this.vehicle.registration_year ?? "",
        brand: this.vehicle.brand || "",
        model: this.vehicle.model || "",
        plate: this.vehicle.plate || "",
        vin: this.vehicle.vin || "",
      };
      this.vehicleExistingPhotos = Array.isArray(this.vehicle.photos) ? [...this.vehicle.photos] : [];
      this.showVehicleModal = true;
    },
    closeVehicleEdit(force = false) {
      if (this.vehicleSaving && !force) return;
      this.showVehicleModal = false;
      this.vehicleFormError = "";
      this.resetPendingVehiclePhotos();
      this.vehicleExistingPhotos = [];
      if (this.$refs.vehiclePhotoInput) {
        this.$refs.vehiclePhotoInput.value = "";
      }
    },
    async saveVehicleEdit() {
      if (!this.vehicleForm.id) return;
      const brand = this.vehicleForm.brand.trim();
      const model = this.vehicleForm.model.trim();
      if (!brand || !model) {
        this.vehicleFormError = "Marca y modelo son obligatorios.";
        return;
      }
      this.vehicleSaving = true;
      this.vehicleFormError = "";
      try {
        const photos = await this.buildVehiclePhotoPayload();
        const payload = {
          type: this.vehicleForm.type,
          registrationYear: this.vehicleForm.registrationYear || null,
          brand,
          model,
          plate: this.vehicleForm.plate || null,
          vin: this.vehicleForm.vin || null,
          photos: photos.length ? photos : null,
        };
        await updateVehicle(this.vehicleForm.id, payload);
        this.closeVehicleEdit(true);
        try {
          await this.loadVehicle();
        } catch (_) {
          // ignore refresh errors after a successful save
        }
      } catch (err) {
        this.vehicleFormError = err?.message || "No se pudo guardar el vehículo.";
      } finally {
        this.vehicleSaving = false;
      }
    },
    onVehiclePhotosSelected(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      const mapped = files.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        name: file.name,
        file,
        preview: URL.createObjectURL(file),
      }));
      this.vehicleSelectedPhotos = [...this.vehicleSelectedPhotos, ...mapped];
      event.target.value = "";
    },
    openVehiclePhotoDialog() {
      this.$refs.vehiclePhotoInput?.click();
    },
    removeVehiclePendingPhoto(index) {
      const [removed] = this.vehicleSelectedPhotos.splice(index, 1);
      if (removed?.preview) {
        URL.revokeObjectURL(removed.preview);
      }
    },
    resetPendingVehiclePhotos() {
      this.vehicleSelectedPhotos.forEach((photo) => {
        if (photo.preview) {
          URL.revokeObjectURL(photo.preview);
        }
      });
      this.vehicleSelectedPhotos = [];
    },
    async removeVehicleExistingPhoto(resourceId) {
      if (!this.vehicle?.id || !resourceId) return;
      try {
        await deleteVehiclePhoto(this.vehicle.id, resourceId);
        this.vehicleExistingPhotos = this.vehicleExistingPhotos.filter((photo) => photo.id !== resourceId);
        if (this.vehicle?.photos) {
          this.vehicle.photos = this.vehicle.photos.filter((photo) => photo.id !== resourceId);
        }
      } catch (err) {
        this.vehicleFormError = err?.message || "No se pudo eliminar la foto.";
      }
    },
    buildVehicleExpenseName(typeId) {
      if (!this.isVehicleHistory) return "";
      const type = this.typesForCategory.find((item) => item.id === Number(typeId));
      const typeName = type?.name || "";
      const brand = this.vehicle?.brand || "";
      const model = this.vehicle?.model || "";
      const parts = [brand, model, typeName].filter(Boolean).join(" ");
      return parts ? `Mantenimiento - ${parts}` : "";
    },
    buildTaxExpenseName(typeId) {
      if (!this.isTaxSection) return "";
      const type = this.typesForCategory.find((item) => item.id === Number(typeId));
      const typeName = type?.name || "";
      return typeName ? `Impuesto - ${typeName}` : "Impuesto";
    },
    updateExpenseNameLocked() {
      if (!this.isVehicleHistory || !this.recurringForm) {
        if (this.recurringForm) this.recurringForm.expenseNameLocked = false;
        return;
      }
      const lockedName = this.buildVehicleExpenseName(this.recurringForm.typeId);
      if (lockedName) {
        this.recurringForm.expenseName = lockedName;
        this.recurringForm.expenseNameLocked = true;
      } else {
        this.recurringForm.expenseNameLocked = false;
      }
    },
    updateTaxExpenseName() {
      if (!this.isTaxSection || !this.recurringForm) return;
      const suggested = this.buildTaxExpenseName(this.recurringForm.typeId);
      if (suggested && !this.recurringForm.expenseName) {
        this.recurringForm.expenseName = suggested;
      }
      this.recurringForm.expenseEnabled = true;
    },
    async buildVehiclePhotoPayload() {
      if (!this.vehicleSelectedPhotos.length) return [];
      const mapped = await Promise.all(
        this.vehicleSelectedPhotos.map(async (item) => ({
          name: item.name,
          type: "IMAGE",
          format: "BASE64",
          data: await this.fileToBase64(item.file),
        }))
      );
      return mapped;
    },
    async removeVehicle() {
      if (!this.resolvedVehicleId || this.deletingVehicle) return;
      const label = this.vehicle
        ? `${this.vehicle.brand || ""} ${this.vehicle.model || ""}`.trim()
        : "este vehículo";
      if (!confirm(`¿Eliminar "${label || "este vehículo"}"?`)) return;
      this.deletingVehicle = true;
      this.vehicleDeleteError = "";
      try {
        await deleteVehicle(this.resolvedVehicleId);
        this.$router.push("/dashboard/maintenance/vehicles");
      } catch (err) {
        this.vehicleDeleteError = err?.message || "No se pudo eliminar el vehículo.";
      } finally {
        this.deletingVehicle = false;
      }
    },
    openDocumentModal(document) {
      this.documentFormError = "";
      this.documentDragActive = false;
      this.resetPendingDocumentFiles();
      if (this.$refs.documentFilesInput) {
        this.$refs.documentFilesInput.value = "";
      }
      if (document) {
        this.documentForm = {
          id: document.id,
          name: document.name || "",
          description: document.description || "",
          uploadedAt: this.normalizeDate(document.uploadedAt),
          expiresAt: this.normalizeDate(document.expiresAt),
        };
        this.documentFiles = document.files ? [...document.files] : [];
      } else {
        this.documentForm = this.emptyDocumentForm();
        this.documentFiles = [];
      }
      this.showDocumentModal = true;
    },
    closeDocumentModal(force = false) {
      if (this.documentSaving && !force) return;
      this.showDocumentModal = false;
      this.documentFormError = "";
      this.documentDragActive = false;
      this.resetPendingDocumentFiles();
      this.documentFiles = [];
      if (this.$refs.documentFilesInput) {
        this.$refs.documentFilesInput.value = "";
      }
    },
    async saveDocument() {
      const name = this.documentForm.name.trim();
      const uploadedAt = this.normalizeDate(this.documentForm.uploadedAt);
      if (!name || !uploadedAt) {
        this.documentFormError = "Nombre y fecha de subida son obligatorios.";
        return;
      }
      if (this.documentSaving) return;
      this.documentSaving = true;
      this.documentFormError = "";
      try {
        const files = await this.buildDocumentFiles();
        const payload = {
          category: this.category,
          name,
          description: this.documentForm.description || null,
          uploadedAt,
          expiresAt: this.normalizeDate(this.documentForm.expiresAt),
          vehicleId: this.resolvedVehicleId,
        };
        let saved = null;
        if (this.documentForm.id) {
          saved = await updateDocument(this.documentForm.id, payload);
        } else {
          saved = await createDocument(payload);
        }
        if (files.length && saved?.id) {
          await uploadDocumentFiles(saved.id, files);
        }
        this.closeDocumentModal(true);
        try {
          await this.loadDocuments();
        } catch (_) {
          // ignore refresh errors after successful save
        }
      } catch (err) {
        this.documentFormError = err?.message || "No se pudo guardar el documento.";
      } finally {
        this.documentSaving = false;
      }
    },
    async removeDocument(document) {
      if (!confirm(`¿Eliminar "${document.name}"?`)) return;
      try {
        await deleteDocument(document.id);
        this.documents = this.documents.filter((item) => item.id !== document.id);
      } catch (err) {
        this.documentsError = err?.message || "No se pudo eliminar el documento.";
      }
    },
    async removeDocumentFromModal() {
      if (!this.documentForm.id) return;
      if (!confirm(`¿Eliminar "${this.documentForm.name}"?`)) return;
      try {
        await deleteDocument(this.documentForm.id);
        this.documents = this.documents.filter((item) => item.id !== this.documentForm.id);
        this.closeDocumentModal();
      } catch (err) {
        this.documentFormError = err?.message || "No se pudo eliminar el documento.";
      }
    },
    async removeExistingDocumentFile(documentId, resourceId) {
      if (!documentId) return;
      try {
        await deleteDocumentFile(documentId, resourceId);
        this.documentFiles = this.documentFiles.filter((file) => file.id !== resourceId);
        this.documents = this.documents.map((doc) =>
          doc.id === documentId
            ? { ...doc, files: (doc.files || []).filter((file) => file.id !== resourceId) }
            : doc
        );
      } catch (err) {
        this.documentFormError = err?.message || "No se pudo eliminar el archivo.";
      }
    },
    async onDocumentFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      await this.appendDocumentFiles(files);
      event.target.value = "";
    },
    openDocumentFileDialog() {
      this.$refs.documentFilesInput?.click();
    },
    onDocumentDragOver() {
      this.documentDragActive = true;
    },
    onDocumentDragLeave() {
      this.documentDragActive = false;
    },
    async onDocumentDrop(event) {
      this.documentDragActive = false;
      const files = Array.from(event.dataTransfer?.files || []);
      if (!files.length) return;
      await this.appendDocumentFiles(files);
    },
    async appendDocumentFiles(files) {
      const mapped = await this.mapDocumentFiles(files);
      this.selectedDocumentFiles = [...this.selectedDocumentFiles, ...mapped];
    },
    async mapDocumentFiles(files) {
      return Promise.all(
        files.map(async (file) => {
          const data = await this.fileToBase64(file);
          return {
            id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
            name: file.name,
            file,
            data,
            preview: file.type?.startsWith("image/") ? data : "",
          };
        })
      );
    },
    removePendingDocumentFile(index) {
      const [removed] = this.selectedDocumentFiles.splice(index, 1);
      if (removed?.preview?.startsWith("blob:")) {
        URL.revokeObjectURL(removed.preview);
      }
    },
    resetPendingDocumentFiles() {
      this.selectedDocumentFiles.forEach((file) => {
        if (file.preview?.startsWith("blob:")) {
          URL.revokeObjectURL(file.preview);
        }
      });
      this.selectedDocumentFiles = [];
    },
    async buildDocumentFiles() {
      if (!this.selectedDocumentFiles.length) return [];
      const mapped = await Promise.all(
        this.selectedDocumentFiles.map(async (item) => ({
          name: item.name,
          type: item.file.type?.startsWith("image/") ? "IMAGE" : "FILE",
          format: "BASE64",
          data: item.data || (await this.fileToBase64(item.file)),
        }))
      );
      return mapped;
    },
    prepareRecordForm(record) {
      this.formError = "";
      this.ticketDragActive = false;
      this.showNewTypeForm = false;
      this.newTypeName = "";
      this.resetPendingTickets();
      if (record) {
        const normalized = this.normalizeRecord(record);
        this.form = {
          id: normalized.id,
          typeId: normalized.typeId || "",
          name: normalized.name || normalized.typeName || "",
          description: normalized.description || "",
          taxIssuer: normalized.taxIssuer || "",
          taxIssueDate: normalized.taxIssueDate || "",
          taxPaymentDate: normalized.taxPaymentDate || "",
          performedAt: normalized.performedAt,
          costEur: Number(normalized.costEur || 0),
        };
        this.existingTickets = Array.isArray(normalized.tickets)
          ? [...normalized.tickets]
          : [];
      } else {
        this.form = this.emptyForm();
        this.existingTickets = [];
      }
      this.$nextTick(() => {
        if (this.$refs.modalFiles) {
          this.$refs.modalFiles.value = "";
        }
      });
    },
    openModal(record) {
      if (this.isHomeSection) {
        this.openHomeModal("ONE_TIME", record);
        return;
      }
      this.prepareRecordForm(record);
      this.showModal = true;
    },
    closeModal(force = false) {
      if (this.saving && !force) return;
      this.showModal = false;
      this.formError = "";
      this.ticketDragActive = false;
      this.resetPendingTickets();
      this.existingTickets = [];
    },
    prepareRecurringForm(item) {
      this.recurringFormError = "";
      this.showNewTypeForm = false;
      this.newTypeName = "";
      if (item) {
        const normalized = this.normalizeRecurring(item);
        const lockedName = this.buildVehicleExpenseName(normalized.typeId) || normalized.expenseName || "";
        this.recurringForm = {
          id: normalized.id,
          typeId: normalized.typeId || "",
          recurrenceValue: normalized.recurrenceValue || 1,
          recurrenceUnit: normalized.recurrenceUnit || "MONTHS",
          notes: normalized.notes || "",
          active: normalized.active !== false,
          expenseEnabled: normalized.expenseEnabled || false,
          expenseRecurringId: normalized.expenseRecurringId || null,
          expenseName: lockedName,
          expenseNameLocked: Boolean(this.isVehicleHistory && lockedName),
          expenseDescription: normalized.expenseDescription || "",
          expenseAmountEur: Number(normalized.expenseAmountEur || 0),
          expenseStartAt: normalized.expenseStartAt || new Date().toISOString().slice(0, 10),
          expenseCurrency: normalized.expenseCurrency || "EUR",
          expenseProviderId: normalized.expenseProviderId ?? null,
          expenseCategoryId: normalized.expenseCategoryId ?? null,
          expenseActive: normalized.expenseActive !== false,
        };
      } else {
        this.recurringForm = this.emptyRecurringForm();
      }
      if (this.isVehicleHistory) {
        this.updateExpenseNameLocked();
      }
      if (this.isTaxSection) {
        this.recurringForm.expenseEnabled = true;
        this.updateTaxExpenseName();
      }
    },
    openRecurringModal(item) {
      if (this.isHomeSection) {
        this.openHomeModal("RECURRING", item);
        return;
      }
      this.prepareRecurringForm(item);
      this.showRecurringModal = true;
    },
    closeRecurringModal(force = false) {
      if (this.recurringSaving && !force) return;
      this.showRecurringModal = false;
      this.recurringFormError = "";
    },
    openHomeModal(mode = "ONE_TIME", item = null) {
      this.homeModalMode = mode;
      if (mode === "RECURRING") {
        this.prepareRecurringForm(item);
      } else {
        this.prepareRecordForm(item);
      }
      this.homeModalOpen = true;
    },
    closeHomeModal(force = false) {
      if ((this.saving || this.recurringSaving) && !force) return;
      this.closeModal(true);
      this.closeRecurringModal(true);
      this.homeModalOpen = false;
      this.formError = "";
      this.recurringFormError = "";
    },
    switchHomeMode(mode) {
      if (this.homeModalMode === mode) return;
      this.homeModalMode = mode;
      this.showNewTypeForm = false;
      this.newTypeName = "";
      this.formError = "";
      this.recurringFormError = "";
      if (mode === "RECURRING") {
        if (!this.recurringForm) this.prepareRecurringForm(null);
      } else {
        if (!this.form) this.prepareRecordForm(null);
      }
    },
    async saveRecurring() {
      if (!this.recurringForm.typeId) {
        this.recurringFormError = "Selecciona un tipo.";
        return;
      }
      if (this.isTaxSection) {
        this.recurringForm.expenseEnabled = true;
      }
      if (!this.recurringForm.recurrenceValue || this.recurringForm.recurrenceValue <= 0) {
        this.recurringFormError = "El periodo debe ser mayor que 0.";
        return;
      }
      if (
        this.supportsExpenseRecurring &&
        this.recurringForm.expenseEnabled &&
        Number(this.recurringForm.expenseAmountEur) < 0
      ) {
        this.recurringFormError = "El importe del gasto debe ser positivo.";
        return;
      }
      const typeId = Number(this.recurringForm.typeId);
      const vehicleId = this.resolvedVehicleId ?? null;
      const existing = this.recurring.find(
        (item) => Number(item.typeId) === typeId && (item.vehicleId ?? null) === vehicleId
      );
      if (!this.recurringForm.id && existing) {
        this.openRecurringModal(existing);
        this.recurringFormError = "Ya existe una recurrencia para ese tipo. Puedes editarla.";
        return;
      }
      if (this.recurringSaving) return;
      this.recurringSaving = true;
      try {
        const payload = {
          typeId,
          recurrenceValue: this.recurringForm.recurrenceValue,
          recurrenceUnit: this.recurringForm.recurrenceUnit,
          notes: this.recurringForm.notes || null,
          active: this.recurringForm.active,
          vehicleId: this.resolvedVehicleId,
        };
        if (this.supportsExpenseRecurring && this.recurringForm.expenseEnabled) {
          const providerId =
            this.recurringForm.expenseProviderId !== null && this.recurringForm.expenseProviderId !== ""
              ? Number(this.recurringForm.expenseProviderId)
              : null;
          const categoryId =
            this.recurringForm.expenseCategoryId !== null && this.recurringForm.expenseCategoryId !== ""
              ? Number(this.recurringForm.expenseCategoryId)
              : null;
          const lockedName = this.isTaxSection
            ? this.buildTaxExpenseName(this.recurringForm.typeId)
            : this.buildVehicleExpenseName(this.recurringForm.typeId);
          payload.expenseRecurring = {
            name: lockedName || this.recurringForm.expenseName || null,
            description: this.recurringForm.expenseDescription || null,
            startAt: this.recurringForm.expenseStartAt || null,
            amountEur: this.recurringForm.expenseAmountEur ?? null,
            currency: this.recurringForm.expenseCurrency || "EUR",
            providerId,
            categoryId,
            active: this.recurringForm.expenseActive !== false,
          };
        }
        let saved;
        if (this.recurringForm.id) {
          saved = await updateRecurringMaintenance(this.recurringForm.id, payload);
          this.recurring = this.recurring.map((item) => (item.id === saved.id ? saved : item));
        } else {
          saved = await createRecurringMaintenance(payload);
          this.recurring = [saved, ...this.recurring];
        }
        if (this.isHomeSection) {
          this.closeHomeModal(true);
        } else {
          this.closeRecurringModal(true);
        }
        try {
          await this.loadRecurring();
        } catch (_) {
          // ignore refresh errors after successful save
        }
      } catch (err) {
        this.recurringFormError = err?.message || "No se pudo guardar la recurrencia.";
      } finally {
        this.recurringSaving = false;
      }
    },
    async removeRecurring(item) {
      const label =
        item.typeName ||
        item.name ||
        this.typesForCategory.find((type) => type.id === item.typeId)?.name ||
        "este mantenimiento";
      if (!confirm(`¿Eliminar recurrencia "${label}"?`)) return;
      try {
        await deleteRecurringMaintenance(item.id);
        this.recurring = this.recurring.filter((rec) => rec.id !== item.id);
        this.upcoming = this.upcoming.filter((rec) => rec.id !== item.id);
      } catch (err) {
        this.recurringError = err?.message || "No se pudo eliminar la recurrencia.";
      }
    },
    toggleNewTypeForm() {
      this.showNewTypeForm = !this.showNewTypeForm;
      if (!this.showNewTypeForm) {
        this.newTypeName = "";
      }
    },
    async createType() {
      const name = this.newTypeName.trim();
      if (!name) return;
      try {
        const saved = await createMaintenanceType({ name, category: this.category });
        this.types = [...this.types, saved].sort((a, b) => a.name.localeCompare(b.name));
        this.form.typeId = saved.id;
        this.recurringForm.typeId = saved.id;
        this.newTypeName = "";
        this.showNewTypeForm = false;
      } catch (err) {
        const message = err?.message || "No se pudo crear el tipo.";
        if (this.showRecurringModal) {
          this.recurringFormError = message;
        } else {
          this.formError = message;
        }
      }
    },
    async saveRecord() {
      let performedAt = this.normalizeDate(this.form.performedAt);
      if (this.isTaxSection && !performedAt) {
        performedAt = this.normalizeDate(this.form.taxPaymentDate);
      }
      if (!this.form.typeId || !performedAt) {
        this.formError = "Tipo y fecha son obligatorios.";
        return;
      }
      if (this.saving) return;
      this.saving = true;
      try {
        let saved;
        const tickets = await this.buildTicketPayload();
        const payload = this.payload(performedAt, tickets);
        if (this.form.id) {
          saved = await updateMaintenance(this.form.id, payload);
          this.records = this.records.map((item) => (item.id === saved.id ? saved : item));
        } else {
          saved = await createMaintenance(payload);
          this.records = [saved, ...this.records];
        }
        if (this.isHomeSection) {
          this.closeHomeModal(true);
        } else {
          this.closeModal(true);
        }
        try {
          await this.loadAll();
        } catch (_) {
          // ignore refresh errors after successful save
        }
      } catch (err) {
        this.formError = err?.message || "No se pudo guardar el mantenimiento.";
      } finally {
        this.saving = false;
      }
    },
    payload(performedAt, tickets) {
      const typeId = this.form.typeId ? Number(this.form.typeId) : null;
      const type = this.typesForCategory.find((item) => item.id === typeId);
      const resolvedName = type?.name || this.form.name || "";
      return {
        category: this.category,
        typeId,
        name: resolvedName,
        description: this.form.description,
        taxIssuer: this.form.taxIssuer || null,
        taxIssueDate: this.normalizeDate(this.form.taxIssueDate),
        taxPaymentDate: this.normalizeDate(this.form.taxPaymentDate),
        performedAt,
        costEur: this.form.costEur,
        tickets,
        vehicleId: this.resolvedVehicleId,
      };
    },
    async removeRecord(record) {
      const label = record.name || record.typeName || "este mantenimiento";
      if (!confirm(`¿Eliminar "${label}"?`)) return;
      try {
        await deleteMaintenance(record.id);
        this.records = this.records.filter((item) => item.id !== record.id);
      } catch (err) {
        this.error = err?.message || "No se pudo eliminar.";
      }
    },
    async removeRecordFromModal() {
      if (!this.form.id) return;
      const typeLabel =
        this.typesForCategory.find((type) => type.id === this.form.typeId)?.name ||
        this.form.name ||
        "este mantenimiento";
      if (!confirm(`¿Eliminar "${typeLabel}"?`)) return;
      try {
        await deleteMaintenance(this.form.id);
        this.records = this.records.filter((item) => item.id !== this.form.id);
        this.closeModal();
      } catch (err) {
        this.formError = err?.message || "No se pudo eliminar.";
      }
    },
    async buildTicketPayload() {
      if (!this.selectedTicketFiles.length) return null;
      const mapped = await Promise.all(
        this.selectedTicketFiles.map(async (item) => ({
          name: item.name,
          type: "IMAGE",
          format: "BASE64",
          data: item.data || (await this.fileToBase64(item.file)),
        }))
      );
      return mapped;
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    async onModalFilesSelected(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length) return;
      await this.appendTicketFiles(files);
      event.target.value = "";
    },
    openTicketFileDialog() {
      this.$refs.modalFiles?.click();
    },
    onTicketDragOver() {
      this.ticketDragActive = true;
    },
    onTicketDragLeave() {
      this.ticketDragActive = false;
    },
    async onTicketDrop(event) {
      this.ticketDragActive = false;
      const files = Array.from(event.dataTransfer?.files || []);
      if (!files.length) return;
      await this.appendTicketFiles(files);
    },
    async appendTicketFiles(files) {
      const mapped = await Promise.all(
        files.map(async (file) => {
          const data = await this.fileToBase64(file);
          return {
            id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
            name: file.name,
            file,
            data,
            url: file.type?.startsWith("image/") ? data : "",
          };
        })
      );
      this.selectedTicketFiles = [...this.selectedTicketFiles, ...mapped];
    },
    removePendingTicket(index) {
      const [removed] = this.selectedTicketFiles.splice(index, 1);
      if (removed?.url?.startsWith("blob:")) {
        URL.revokeObjectURL(removed.url);
      }
    },
    resetPendingTickets() {
      this.selectedTicketFiles.forEach((ticket) => {
        if (ticket.url?.startsWith("blob:")) {
          URL.revokeObjectURL(ticket.url);
        }
      });
      this.selectedTicketFiles = [];
      if (this.$refs.modalFiles) {
        this.$refs.modalFiles.value = "";
      }
    },
    selectTicketUpload(id) {
      this.ticketUploadId = id;
      if (this.$refs.ticketInput) {
        this.$refs.ticketInput.value = "";
        this.$refs.ticketInput.click();
      }
    },
    async uploadTickets(event) {
      const files = Array.from(event.target.files || []);
      if (!files.length || !this.ticketUploadId) return;
      try {
        const tickets = await Promise.all(
          files.map(async (file) => ({
            name: file.name,
            type: "IMAGE",
            format: "BASE64",
            data: await this.fileToBase64(file),
          }))
        );
        await uploadMaintenanceTickets(this.ticketUploadId, tickets);
        await this.loadAll();
      } catch (err) {
        this.error = err?.message || "No se pudieron subir los tickets.";
      } finally {
        this.ticketUploadId = null;
      }
    },
    async removeTicket(recordId, ticketId) {
      if (!confirm("¿Eliminar este ticket?")) return;
      try {
        await deleteMaintenanceTicket(recordId, ticketId);
        await this.loadAll();
      } catch (err) {
        this.error = err?.message || "No se pudo eliminar el ticket.";
      }
    },
    async removeExistingTicket(ticketId) {
      if (!this.form.id) return;
      if (!confirm("¿Eliminar este ticket?")) return;
      try {
        await deleteMaintenanceTicket(this.form.id, ticketId);
        this.existingTickets = this.existingTickets.filter((ticket) => ticket.id !== ticketId);
        await this.loadAll();
      } catch (err) {
        this.formError = err?.message || "No se pudo eliminar el ticket.";
      }
    },
    handleRecordClick(record) {
      if (this.isHomeSection) {
        this.openHomeModal("ONE_TIME", record);
        return;
      }
      this.openModal(record);
    },
    formatDate(value) {
      if (!value) return "—";
      return new Intl.DateTimeFormat("es-ES", { dateStyle: "medium" }).format(new Date(value));
    },
    todayIso() {
      return new Date().toISOString().slice(0, 10);
    },
    timeRemaining(value) {
      if (!value) return "—";
      const target = new Date(value);
      const today = new Date();
      target.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (diffDays === 0) return "hoy";
      const absDays = Math.abs(diffDays);
      let count = absDays;
      let unit = "día";
      if (absDays >= 365) {
        count = Math.round(absDays / 365);
        unit = count === 1 ? "año" : "años";
      } else if (absDays >= 30) {
        count = Math.round(absDays / 30);
        unit = count === 1 ? "mes" : "meses";
      } else if (absDays >= 7) {
        count = Math.round(absDays / 7);
        unit = count === 1 ? "semana" : "semanas";
      } else {
        unit = count === 1 ? "día" : "días";
      }
      return diffDays > 0 ? `en ${count} ${unit}` : `hace ${count} ${unit}`;
    },
    normalizeDate(value) {
      if (!value) return null;
      if (typeof value !== "string") {
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return date.toISOString().slice(0, 10);
      }
      if (value.includes("T")) {
        return value.slice(0, 10);
      }
      if (value.includes("-")) {
        return value;
      }
      const parts = value.split("/");
      if (parts.length !== 3) return null;
      const [day, month, year] = parts;
      if (!day || !month || !year) return null;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    },
    normalizeRecord(record) {
      if (!record) return record;
      const typeId = record.typeId ?? record.type_id ?? null;
      const typeName = record.typeName ?? record.type_name ?? "";
        return {
          ...record,
          typeId,
          typeName,
          name: record.name || typeName,
          performedAt: this.normalizeDate(record.performedAt ?? record.performed_at),
          nextDueAt: this.normalizeDate(record.nextDueAt ?? record.next_due_at),
          costEur: record.costEur ?? record.cost_eur,
          taxIssuer: record.taxIssuer ?? record.tax_issuer ?? "",
          taxIssueDate: this.normalizeDate(record.taxIssueDate ?? record.tax_issue_date),
          taxPaymentDate: this.normalizeDate(record.taxPaymentDate ?? record.tax_payment_date),
          vehicleId: record.vehicleId ?? record.vehicle_id ?? null,
        };
      },
    normalizeRecurring(item) {
      if (!item) return item;
      const typeId = item.typeId ?? item.type_id ?? null;
      const typeName = item.typeName ?? item.type_name ?? "";
      const expenseRecurringId = item.expenseRecurringId ?? item.expense_recurring_id ?? null;
      const expense = expenseRecurringId ? this.expenseRecurringById[expenseRecurringId] : null;
      return {
        ...item,
        typeId,
        typeName,
        name: item.name || typeName,
        notes: item.notes ?? item.description ?? "",
        recurrenceValue: item.recurrenceValue ?? item.recurrence_value,
        recurrenceUnit: item.recurrenceUnit ?? item.recurrence_unit,
        lastPerformedAt: this.normalizeDate(item.lastPerformedAt ?? item.last_performed_at),
        nextDueAt: this.normalizeDate(item.nextDueAt ?? item.next_due_at),
        vehicleId: item.vehicleId ?? item.vehicle_id ?? null,
        expenseRecurringId,
        expenseEnabled: Boolean(expenseRecurringId),
        expenseName: expense?.name || "",
        expenseDescription: expense?.description || "",
        expenseAmountEur: Number(expense?.amountEur ?? expense?.amount_eur ?? 0),
        expenseStartAt: this.normalizeDate(expense?.startAt ?? expense?.start_at) || "",
        expenseCurrency: expense?.currency || "EUR",
        expenseProviderId: expense?.provider?.id ?? expense?.providerId ?? expense?.provider_id ?? null,
        expenseCategoryId: expense?.category?.id ?? expense?.categoryId ?? expense?.category_id ?? null,
        expenseActive: expense?.active !== false,
        active: item.active !== false,
      };
    },
    normalizeDocument(document) {
      if (!document) return document;
      return {
        ...document,
        uploadedAt: this.normalizeDate(document.uploadedAt ?? document.uploaded_at),
        expiresAt: this.normalizeDate(document.expiresAt ?? document.expires_at),
        files: document.files || [],
      };
    },
    formatEur(value) {
      if (value == null) return "—";
      return new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
      }).format(Number(value));
    },
    recurrenceLabel(item) {
      const value = item.recurrenceValue || 1;
      const unit = item.recurrenceUnit || "MONTHS";
      const labels = {
        DAYS: "días",
        WEEKS: "semanas",
        MONTHS: "meses",
        YEARS: "años",
      };
      return `Cada ${value} ${labels[unit] || ""}`;
    },
    filterMaintenanceItems(items) {
      if (!this.searchQuery) return items;
      return items.filter((item) => this.maintenanceMatches(item));
    },
    maintenanceMatches(item) {
      const query = this.searchQuery;
      if (!query) return true;
      const ticketNames = Array.isArray(item.tickets)
        ? item.tickets.map((ticket) => ticket.name || "")
        : [];
      const haystack = [
        item.name,
        item.typeName,
        item.description,
        item.notes,
        item.performedAt ? this.formatDate(item.performedAt) : "",
        item.lastPerformedAt ? this.formatDate(item.lastPerformedAt) : "",
        item.nextDueAt ? this.formatDate(item.nextDueAt) : "",
        item.costEur != null ? String(item.costEur) : "",
        item.recurrenceValue ? this.recurrenceLabel(item) : "",
        ...ticketNames,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    },
    documentMatches(doc) {
      const query = this.searchQuery;
      if (!query) return true;
      const fileNames = Array.isArray(doc.files) ? doc.files.map((file) => file.name || "") : [];
      const haystack = [
        doc.name,
        doc.description,
        doc.uploadedAt ? this.formatDate(doc.uploadedAt) : "",
        doc.expiresAt ? this.formatDate(doc.expiresAt) : "",
        ...fileNames,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    },
    openLightbox(src, alt) {
      if (!src) return;
      let images = [];
      let startIndex = 0;
      let label = alt || "Imagen";
      if (Array.isArray(src)) {
        images = src
          .map((item) =>
            typeof item === "string"
              ? { src: item, alt: label }
              : { src: item.src || item.data || "", alt: item.alt || label || item.name || "Imagen" }
          )
          .filter((item) => item.src);
      } else if (typeof src === "object" && src.images) {
        images = (src.images || [])
          .map((item) =>
            typeof item === "string"
              ? { src: item, alt: label }
              : { src: item.src || item.data || "", alt: item.alt || label || item.name || "Imagen" }
          )
          .filter((item) => item.src);
        startIndex = Number.isInteger(src.index) ? src.index : 0;
        label = src.title || label;
      } else {
        images = [{ src, alt: label }];
      }
      if (!images.length) return;
      this.lightboxImages = images;
      this.lightboxIndex = Math.min(Math.max(startIndex, 0), images.length - 1);
      this.lightboxAlt = label;
      this.lightboxTouchStartX = 0;
    },
    closeLightbox() {
      this.lightboxImages = [];
      this.lightboxIndex = 0;
      this.lightboxAlt = "";
      this.lightboxTouchStartX = 0;
    },
    setLightboxIndex(index) {
      if (!this.lightboxImages.length) return;
      const next = Math.min(Math.max(index, 0), this.lightboxImages.length - 1);
      this.lightboxIndex = next;
    },
    nextLightboxImage() {
      if (!this.lightboxImages.length) return;
      const next = (this.lightboxIndex + 1) % this.lightboxImages.length;
      this.lightboxIndex = next;
    },
    prevLightboxImage() {
      if (!this.lightboxImages.length) return;
      const prev =
        (this.lightboxIndex - 1 + this.lightboxImages.length) % this.lightboxImages.length;
      this.lightboxIndex = prev;
    },
    onLightboxTouchStart(event) {
      const touch = event.touches?.[0];
      if (!touch) return;
      this.lightboxTouchStartX = touch.clientX;
    },
    onLightboxTouchEnd(event) {
      const touch = event.changedTouches?.[0];
      if (!touch || !this.lightboxTouchStartX) return;
      const diff = touch.clientX - this.lightboxTouchStartX;
      const threshold = 40;
      if (Math.abs(diff) > threshold) {
        if (diff < 0) {
          this.nextLightboxImage();
        } else {
          this.prevLightboxImage();
        }
      }
      this.lightboxTouchStartX = 0;
    },
  },
};
</script>

<style scoped>
.maintenance {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.maintenance h1 {
  font-size: var(--font-size-h1);
  letter-spacing: -0.02em;
}

.maintenance h2 {
  font-size: var(--font-size-h2);
  letter-spacing: -0.01em;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.section-header h1 {
  margin: 0 0 6px;
}

.section-search {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: -10px;
}

.search-field {
  width: min(480px, 100%);
  position: relative;
}

.search-field input {
  width: 100%;
  background: var(--color-card);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 10px 40px 10px 40px;
  color: var(--text);
}

.search-field input:focus-visible {
  outline: 2px solid rgba(56, 189, 248, 0.7);
  outline-offset: 2px;
}

.search-field input::placeholder {
  color: var(--muted);
}

.search-field input::-webkit-search-cancel-button,
.search-field input::-webkit-search-decoration {
  -webkit-appearance: none;
  appearance: none;
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--muted);
  width: 18px;
  height: 18px;
  display: grid;
  place-items: center;
  pointer-events: none;
}

.search-icon svg {
  width: 18px;
  height: 18px;
}

.search-clear {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid rgba(148, 163, 184, 0.3);
  background: rgba(148, 163, 184, 0.12);
  color: var(--text);
  cursor: pointer;
  line-height: 1;
}

.search-clear:hover {
  border-color: rgba(56, 189, 248, 0.5);
}

.upcoming,
.records {
  display: grid;
  gap: 16px;
}

.documents {
  display: grid;
  gap: 16px;
}

.section-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.recurring-section {
  display: grid;
  gap: 16px;
}

.record-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.record-meta {
  display: grid;
  gap: 4px;
  color: var(--muted);
  font-size: 13px;
}

.record-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.type-actions {
  display: flex;
  justify-content: flex-end;
}

.type-new {
  align-items: flex-end;
}

.photo-dropzone {
  height: 120px;
  border-radius: 16px;
  border: 2px dashed rgba(148, 163, 184, 0.4);
  background: rgba(15, 23, 42, 0.4);
  display: grid;
  place-items: center;
  gap: 6px;
  color: var(--muted);
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.photo-dropzone:hover {
  border-color: rgba(56, 189, 248, 0.6);
  background: rgba(56, 189, 248, 0.08);
}

.photo-dropzone__plus {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(148, 163, 184, 0.35);
  display: grid;
  place-items: center;
  font-size: 20px;
  color: var(--text);
}

.photo-dropzone__text {
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.modal-body input.readonly {
  opacity: 0.7;
  cursor: not-allowed;
}

.expense-panel {
  margin-top: 12px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.35);
  display: grid;
  gap: 12px;
}

.documents-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.records-grid.full-row {
  grid-template-columns: 1fr;
}

.document-card {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 16px;
  display: grid;
  gap: 12px;
  box-shadow: var(--shadow);
}

.document-card.clickable {
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.document-card.clickable:hover {
  border-color: rgba(56, 189, 248, 0.4);
  transform: translateY(-1px);
}

.document-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.document-row h3 {
  margin: 0;
}

.document-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  background: rgba(56, 189, 248, 0.16);
  color: var(--accent);
  border: 1px solid rgba(56, 189, 248, 0.3);
  flex: 0 0 auto;
}

.document-icon svg {
  width: 18px;
  height: 18px;
}

.document-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.document-meta {
  display: grid;
  gap: 6px;
}

.document-files {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.file-chip {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: var(--text);
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.file-fallback {
  font-size: 12px;
  color: var(--muted);
  text-align: center;
  padding: 12px 8px;
}

.chip.danger {
  background: rgba(239, 68, 68, 0.15);
  color: var(--danger);
}

.upcoming-grid,
.records-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.upcoming-card,
.record-card,
.empty-card {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 16px;
  box-shadow: var(--shadow);
}

.record-card {
  display: grid;
  gap: 12px;
}

.upcoming-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.upcoming-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.upcoming-text {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.upcoming-text h3 {
  margin: 0;
}

.upcoming-text .muted {
  font-size: 13px;
}

.upcoming-card h3 {
  margin: 0;
}

.upcoming-icon {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  background: rgba(56, 189, 248, 0.16);
  color: var(--accent);
  border: 1px solid rgba(56, 189, 248, 0.35);
  flex: 0 0 auto;
}

.upcoming-icon svg {
  width: 22px;
  height: 22px;
}

.time-remaining {
  color: var(--muted);
  font-size: 13px;
  font-weight: 600;
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.record-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.chip {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.2);
  color: var(--text);
}

.chip.ghost {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.desc {
  margin: 10px 0;
  color: var(--muted);
}

.next-due {
  margin-bottom: 10px;
  font-size: 13px;
}

.tickets {
  display: grid;
  gap: 8px;
}

.ticket-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
}

.ticket-preview {
  display: grid;
  gap: 8px;
}

.ticket-preview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
}

.ticket-preview-item,
.ticket-item {
  position: relative;
  aspect-ratio: 4 / 3;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  background: rgba(15, 23, 42, 0.35);
  overflow: hidden;
  display: grid;
  place-items: center;
}

.ticket-preview-item img,
.ticket-item img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 6px;
  display: block;
}

.ticket-remove {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(15, 23, 42, 0.75);
  color: #fff;
  cursor: pointer;
}

.record-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.record-card.clickable {
  cursor: pointer;
  transition: border-color 0.2s ease, transform 0.2s ease;
}

.record-card.clickable:hover {
  border-color: rgba(56, 189, 248, 0.4);
  transform: translateY(-1px);
}

.primary {
  background: linear-gradient(120deg, var(--accent), #22c55e);
  color: #071022;
  border: none;
  padding: 10px 16px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
  min-width: 120px;
}

.ghost {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text);
  padding: 8px 12px;
  border-radius: 999px;
  cursor: pointer;
  min-width: 120px;
  text-align: center;
}

.ghost.danger {
  color: var(--danger);
  border-color: rgba(248, 113, 113, 0.4);
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(6, 10, 18, 0.65);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: 20;
}

.modal {
  width: min(520px, 100%);
  max-width: calc(100vw - 32px);
  background: var(--panel);
  border-radius: 20px;
  border: 1px solid var(--border);
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: calc(100vh - 48px);
  overflow: hidden;
}

.modal-toggle {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.modal-toggle .ghost {
  min-width: 0;
  padding: 8px 16px;
}

.modal-toggle .ghost.active {
  background: rgba(56, 189, 248, 0.12);
  border-color: rgba(56, 189, 248, 0.45);
  color: var(--color-accent);
}

.modal.is-blocked {
  position: relative;
}

.modal-body.is-disabled {
  opacity: 0.6;
  pointer-events: none;
}

.modal-blocker {
  position: absolute;
  inset: 0;
  border-radius: 20px;
  background: rgba(6, 10, 18, 0.55);
  backdrop-filter: blur(2px);
  z-index: 2;
}

.modal footer {
  display: flex;
  justify-content: flex-end;
  gap: 14px;
  flex-wrap: wrap;
}

.modal footer button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-spinner {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: rgba(255, 255, 255, 0.9);
  display: inline-block;
  margin-right: 8px;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
}

.modal header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-body {
  display: grid;
  gap: 12px;
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  padding-right: 4px;
}

.modal-body label {
  display: grid;
  gap: 6px;
  color: var(--muted);
  font-size: 14px;
  min-width: 0;
}

.modal-body label.checkbox {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.modal-body input,
.modal-body textarea,
.modal-body select {
  background: var(--panel-2);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px;
  color: var(--text);
  width: 100%;
  max-width: 100%;
}

.modal-body input[type="file"] {
  display: block;
  min-width: 0;
}

.modal-dropzone {
  border: 1px dashed rgba(148, 163, 184, 0.4);
  border-radius: 14px;
  padding: 14px;
  display: grid;
  gap: 10px;
  background: rgba(15, 23, 42, 0.45);
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
}

.modal-dropzone:hover {
  transform: translateY(-1px);
  border-color: rgba(56, 189, 248, 0.4);
}

.modal-dropzone.is-active {
  border-color: rgba(56, 189, 248, 0.7);
  background: rgba(56, 189, 248, 0.08);
}

.modal-dropzone__icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: rgba(56, 189, 248, 0.16);
  color: var(--color-accent);
  display: grid;
  place-items: center;
  font-size: 12px;
}

.modal-dropzone__text p {
  margin: 0 0 4px;
}

.modal-dropzone__text span {
  color: var(--color-accent);
  font-weight: 600;
}

.modal-dropzone__text small {
  color: var(--muted);
}

.checkbox input {
  display: none;
}

.checkbox span {
  width: 16px;
  height: 16px;
  border: 1px solid var(--border);
  border-radius: 4px;
  display: inline-block;
  position: relative;
}

.checkbox input:checked + span {
  background: rgba(34, 197, 94, 0.4);
  border-color: rgba(34, 197, 94, 0.6);
}

.checkbox.line {
  margin-top: 8px;
}

.row {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

@media (max-width: 720px) {
  .modal-backdrop {
    padding: 12px 10px;
    align-items: stretch;
  }

  .modal {
    width: 100%;
    max-width: 100%;
    max-height: calc(100vh - 24px);
    border-radius: 16px;
    padding: 16px;
  }

  .modal footer {
    flex-direction: column;
    align-items: stretch;
  }

  .modal footer button {
    width: 100%;
  }

  .row {
    grid-template-columns: 1fr;
  }
}


.icon {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--muted);
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;
}

.icon.danger {
  color: var(--danger);
}

.state {
  padding: 24px;
  text-align: center;
  color: var(--muted);
}

.state.error {
  color: var(--danger);
}

.vehicle-danger {
  margin-top: 8px;
}

.danger-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-radius: 18px;
  border: 1px dashed rgba(248, 113, 113, 0.4);
  background: rgba(15, 23, 42, 0.6);
  box-shadow: var(--shadow);
}

.danger-card h2 {
  margin: 0 0 6px;
}

.danger-card .muted {
  margin: 0;
}

.danger-card button {
  min-width: 180px;
}

@media (max-width: 720px) {
  .danger-card {
    flex-direction: column;
    align-items: flex-start;
  }

  .danger-card button {
    width: 100%;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.form-error {
  margin: 0;
  color: var(--danger);
  font-size: 13px;
}

.empty-card {
  text-align: center;
  color: var(--muted);
}

.lightbox {
  position: fixed;
  inset: 0;
  background: rgba(3, 6, 14, 0.85);
  display: grid;
  place-items: center;
  z-index: 40;
  padding: 24px;
}

.lightbox-content {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 16px;
  width: min(1020px, 92vw);
}

.lightbox-media {
  display: grid;
  place-items: center;
  background: rgba(15, 23, 42, 0.65);
  border-radius: 18px;
  padding: 12px;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.45);
  width: 100%;
  touch-action: pan-y;
}

.lightbox-media img {
  max-width: min(880px, 80vw);
  max-height: min(80vh, 620px);
  object-fit: contain;
  border-radius: 12px;
  display: block;
}

.lightbox-close {
  position: absolute;
  top: -14px;
  right: -14px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(15, 23, 42, 0.85);
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  z-index: 2;
}

.lightbox-nav {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: rgba(15, 23, 42, 0.8);
  color: #fff;
  font-size: 24px;
  cursor: pointer;
  display: grid;
  place-items: center;
}

.lightbox-dots {
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
}

.lightbox-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.4);
  background: transparent;
  cursor: pointer;
}

.lightbox-dot.active {
  background: var(--color-accent);
  border-color: var(--color-accent);
}

.lightbox-thumbs {
  grid-column: 1 / -1;
  display: none;
  gap: 10px;
  margin-top: 10px;
  overflow-x: auto;
  padding-bottom: 6px;
  -webkit-overflow-scrolling: touch;
}

.lightbox-thumb {
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(15, 23, 42, 0.6);
  border-radius: 12px;
  padding: 4px;
  width: 72px;
  height: 52px;
  flex: 0 0 auto;
  cursor: pointer;
}

.lightbox-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.lightbox-thumb.active {
  border-color: var(--color-accent);
}

@media (max-width: 900px) {
  .lightbox-content {
    grid-template-columns: 1fr;
    gap: 10px;
    width: min(94vw, 720px);
  }

  .lightbox-nav {
    display: none;
  }

  .lightbox-thumbs {
    display: flex;
  }

  .lightbox-media img {
    max-width: min(90vw, 620px);
    max-height: 60vh;
  }
}
</style>
